import { service } from '@ember/service';
import Component from '@glimmer/component';
import type AbilitiesService from 'ember-can/services/abilities';
import ViewProfileLink from 'garaje/components/visitors/profiles/view-profile-link';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';

interface IdScanningCardSignature {
  Args: {
    /** The linked visitor profile, or null/undefined when the visitor has none. */
    visitorProfileId?: string | null;
    /** Entry/invite id, forwarded for back navigation. */
    sourceId: string;
    /** 'entry' | 'invite' — the surface the link is returning to. */
    from: string;
  };
  Element: HTMLDivElement;
}

/**
 * "ID Scanning" block in the entry/invite Details sidebar, styled after
 * LegalDocumentActivity. Renders only when the visitor's identity document is
 * actually retrievable from the profile — the whole point of the link.
 */
export default class IdScanningCard extends Component<IdScanningCardSignature> {
  @service declare abilities: AbilitiesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;

  /**
   * The per-location "save ID document on profile" toggle (VIS-17395). Without it
   * the profile holds no identity document, so the link would lead nowhere.
   * Undefined until the visitors route resolves the config — fail-closed.
   */
  get storesIdentityDocumentOnProfile(): boolean {
    return Boolean(this.state.currentLocation?.idScanConfiguration?.storeIdentityDocumentOnProfile);
  }

  /** Non-null by the time the card renders; `showCard` gates on it. */
  get profileId(): string {
    return this.args.visitorProfileId ?? '';
  }

  get showCard(): boolean {
    return (
      Boolean(this.args.visitorProfileId) &&
      this.storesIdentityDocumentOnProfile &&
      this.featureFlags.isEnabled('vis-profile-identity-tab') &&
      this.featureFlags.isEnabled('vis-visitor-profile-show-rrc') &&
      this.abilities.can('read visitor-profile') &&
      this.abilities.can('enable id-scanning') &&
      // Same gate the Identity tab itself uses. Without it this link is a dead affordance:
      // the profile route clamps ?tab=identity back to visits for an unauthorized user, and
      // the card's presence would still disclose that the visitor has an ID on file.
      this.abilities.can('read-profile-identity-documents id-scanning')
    );
  }

  <template>
    {{#if this.showCard}}
      <div class="border-b pb-4 mb-4" data-test-id-scanning-card ...attributes>
        <h4 class="text-sm capitalize">ID scanning</h4>
        <ul class="list-none p-0 m-0 leading-normal mt-2">
          <li class="mb-1 ml-6 text-sm text-carbon-70 list-inside" style="text-indent: -1.5rem">
            <img src="/assets/images/checkmark-small-gray.svg" class="mr-2" alt="" role="presentation" />
            Identity document
            <span class="text-carbon-50">
              - saved to profile
            </span>
          </li>
        </ul>
        <div class="mt-3">
          <ViewProfileLink
            @from={{@from}}
            @sourceId={{@sourceId}}
            @visitorId={{this.profileId}}
            @tab="identity"
            @label="View Identity document"
            @variant="text"
          />
        </div>
      </div>
    {{/if}}
  </template>
}
